<template>
	<div>
		<h6 class="fw-semibold small mb-0">Trap counts near {{ locationLabel }}</h6>
		<div class="text-muted small mb-2">
			Monthly adult trap counts — {{ REPORT_PERIOD }}
		</div>

		<div v-if="traps.length === 0" class="text-muted small">
			No traps near this location.
		</div>
		<template v-else>
			<div class="ia-chart-wrap">
				<Line :data="chartData" :options="chartOptions" />
			</div>

			<div class="small text-muted mt-2">
				Peak season: Jul – Sep. Counts fall to near zero Nov – Mar.
			</div>

			<ul class="list-unstyled small mb-0 mt-2">
				<li
					v-for="s in series"
					:key="s.trap.id"
					class="d-flex align-items-center gap-1"
				>
					<span class="ia-chip" :style="{ backgroundColor: s.color }"></span>
					<span class="text-truncate">{{ s.trap.name }}</span>
					<span class="text-muted ms-auto ps-1">{{ s.km }} km</span>
				</li>
			</ul>
		</template>
	</div>
</template>

<script setup lang="ts">
// MOCK panel: the monthly trap counts come from the frozen fixture in
// `@/mock/invasive-aedes`; nothing here talks to a backend.
import { computed } from "vue";
import { Line } from "vue-chartjs";
import {
	Chart as ChartJS,
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
} from "chart.js";
import type { ChartData, ChartOptions } from "chart.js";
import {
	MONTH_LABELS,
	REPORT_PERIOD,
	distanceKm,
	nearestTraps,
} from "@/mock/invasive-aedes";

ChartJS.register(
	LineElement,
	PointElement,
	CategoryScale,
	LinearScale,
	Tooltip,
	Legend,
);

interface Props {
	location: { latitude: number; longitude: number };
	locationLabel: string;
}

const props = defineProps<Props>();

/** One colour per plotted trap, in distance order (nearest first). */
const PALETTE = ["#0d6efd", "#dc3545", "#198754", "#fd7e14"] as const;

/** The palette colour as an `rgba()` string, for the point/legend fill. */
function withAlpha(hex: string, alpha: number): string {
	const value = Number.parseInt(hex.slice(1), 16);
	const red = (value >> 16) & 0xff;
	const green = (value >> 8) & 0xff;
	const blue = value & 0xff;
	return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

const traps = computed(() =>
	nearestTraps(props.location.latitude, props.location.longitude, 4),
);

/** The plotted traps, each with its dataset colour and distance from here. */
const series = computed(() =>
	traps.value.map((trap, index) => ({
		trap,
		color: PALETTE[index % PALETTE.length],
		km: distanceKm(
			props.location.latitude,
			props.location.longitude,
			trap.latitude,
			trap.longitude,
		).toFixed(1),
	})),
);

const chartData = computed<ChartData<"line">>(() => ({
	labels: [...MONTH_LABELS],
	datasets: series.value.map(({ trap, color }) => ({
		label: trap.name,
		data: trap.counts,
		borderColor: color,
		backgroundColor: withAlpha(color, 0.15),
		borderWidth: 2,
		tension: 0.35,
		pointRadius: 2,
		pointHoverRadius: 4,
		fill: false,
	})),
}));

const chartOptions: ChartOptions<"line"> = {
	responsive: true,
	maintainAspectRatio: false,
	interaction: { mode: "index", intersect: false },
	plugins: {
		legend: {
			position: "bottom",
			labels: { boxWidth: 12, font: { size: 10 } },
		},
		tooltip: { enabled: true },
	},
	scales: {
		x: { grid: { display: false } },
		y: {
			beginAtZero: true,
			title: {
				display: true,
				text: "Adults per trap-night",
				font: { size: 10 },
			},
			ticks: { precision: 0 },
		},
	},
};
</script>

<style scoped>
.ia-chart-wrap {
	position: relative;
	height: 300px;
}
.ia-chip {
	display: inline-block;
	flex: 0 0 auto;
	width: 10px;
	height: 10px;
	border-radius: 2px;
}
</style>
